import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { sv } from 'date-fns/locale/sv';
import { da } from 'date-fns/locale/da';
import { fi } from 'date-fns/locale/fi';
import { pl } from 'date-fns/locale/pl';
import { nb } from 'date-fns/locale/nb';
import { Calendar } from '#icons/Calendar';
import { SimpleTooltip } from '../../../Data/Tooltip/SimpleTooltip';
import { IconButton } from '../../../Input/IconButton';
import { InputWrapper, StyledInputField, IconWrapper } from '../Datepicker/styles';
import '../Datepicker/datepickerOverride.css';
import { YearButtonList } from '../components/YearButtonList';
import {
  YearSection,
  YearMonthPickerWrapper,
  LabelContainer,
  LabelWrapper,
  PickerLabel,
  RequiredIndicator,
  ErrorMessageBox,
  DropdownContainer,
  DropdownPanel,
  MonthPickerContainer,
  MultiYearMessage,
  SectionTitle,
} from './styles';

registerLocale('sv', sv);
registerLocale('da', da);
registerLocale('fi', fi);
registerLocale('pl', pl);
registerLocale('no', nb);

export type YearMonthPickerResult = {
  startDate: Date;
  endDate: Date;
};

export type YearMonthPickerProps = {
  label?: string;
  /** Earliest selectable year. Defaults to 2022. */
  minYear?: number;
  /** Latest selectable year. Defaults to current year. */
  maxYear?: number;
  disabled?: boolean;
  required?: boolean;
  placeholderText?: string;
  errorMessage?: string;
  tooltip?: string;
  /** Optional title shown above the year buttons, with a border beneath it. */
  yearPickerTitle?: string;
  /** Optional title shown above the month picker, with a border beneath it. When provided the separator border between year and month sections is removed. */
  monthPickerTitle?: string;
  /** When true, selecting a single year automatically selects all available months for that year. */
  autoSelectAllMonthsOnSingleYear?: boolean;
  /** When false, clicking the selected year does not clear the selection. Defaults to true. */
  allowClear?: boolean;
  onChange: (result: YearMonthPickerResult | null) => void;
};

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth(); // 0-indexed

const padMonth = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const formatResult = (
  selectedYears: number[],
  monthStart: Date | null,
  monthEnd: Date | null,
): string => {
  if (selectedYears.length === 0) {
    return '';
  }

  const sorted = [...selectedYears].sort((a, b) => a - b);

  if (sorted.length === 2) {
    return `${sorted[0]} → ${sorted[1]}`;
  }

  const year = sorted[0];

  if (monthStart && monthEnd) {
    if (monthStart.getMonth() === monthEnd.getMonth()) {
      return padMonth(monthStart);
    }

    return `${padMonth(monthStart)} → ${padMonth(monthEnd)}`;
  }

  if (monthStart) {
    return `${padMonth(monthStart)} ...`;
  }

  return `${year}`;
};

export const YearMonthPicker = ({
  label,
  minYear = 2022,
  maxYear = currentYear,
  disabled,
  required,
  placeholderText,
  errorMessage,
  tooltip,
  yearPickerTitle,
  monthPickerTitle,
  autoSelectAllMonthsOnSingleYear = false,
  allowClear = true,
  onChange,
}: YearMonthPickerProps) => {
  const { i18n } = useTranslation();
  const resolvedLanguage = i18n.resolvedLanguage ?? 'en';

  const [open, setOpen] = useState(false);

  // Default: current year selected
  const [selectedYears, setSelectedYears] = useState<number[]>([currentYear]);

  // Default: Jan 1 → last day of current month
  const [monthStart, setMonthStart] = useState<Date | null>(new Date(currentYear, 0, 1));
  const [monthEnd, setMonthEnd] = useState<Date | null>(new Date(currentYear, currentMonth + 1, 0));

  const containerRef = useRef<HTMLDivElement>(null);

  // Emit default value on mount
  useEffect(() => {
    onChange({
      startDate: new Date(currentYear, 0, 1),
      endDate: new Date(currentYear, currentMonth + 1, 0),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const years = useMemo(
    () => Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i),
    [minYear, maxYear],
  );

  const isSingleYear = selectedYears.length === 1;
  const isMultiYear = selectedYears.length === 2;

  const handleYearClick = useCallback(
    (year: number | null) => {
      if (year === null) {
        return;
      }

      setSelectedYears((prev) => {
        if (prev.length === 1 && prev[0] === year) {
          if (!allowClear) return prev;

          setMonthStart(null);
          setMonthEnd(null);
          onChange(null);

          return [];
        }

        if (prev.length === 0 || prev.length === 2) {
          // Determine the available month range for the selected year
          const endMonth =
            autoSelectAllMonthsOnSingleYear && year === currentYear ? currentMonth : 11;
          const start = new Date(year, 0, 1);
          const end = new Date(year, endMonth + 1, 0); // last day of endMonth

          if (autoSelectAllMonthsOnSingleYear) {
            setMonthStart(start);
            setMonthEnd(end);
          } else {
            setMonthStart(null);
            setMonthEnd(null);
          }

          onChange({ startDate: start, endDate: end });

          return [year];
        }

        const sorted = [prev[0], year].sort((a, b) => a - b);

        setMonthStart(null);
        setMonthEnd(null);

        onChange({
          startDate: new Date(sorted[0], 0, 1),
          endDate: new Date(sorted[1], 11, 31),
        });

        return sorted;
      });
    },
    [onChange, autoSelectAllMonthsOnSingleYear, allowClear],
  );

  const handleMonthRangeChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      const [start, end] = dates;

      setMonthStart(start);
      setMonthEnd(end);

      if (start && end && selectedYears.length === 1) {
        onChange({
          startDate: new Date(start.getFullYear(), start.getMonth(), 1),
          endDate: new Date(end.getFullYear(), end.getMonth() + 1, 0),
        });
      }
    },
    [onChange, selectedYears],
  );

  const singleYear = isSingleYear ? selectedYears[0] : null;

  // For the selected year, cap maxDate at end of current month if it's the current year
  const monthPickerMaxDate = useMemo(() => {
    if (singleYear === currentYear) {
      return new Date(currentYear, currentMonth + 1, 0); // last day of current month
    }

    return singleYear !== null ? new Date(singleYear, 11, 31) : undefined;
  }, [singleYear]);

  const displayValue = formatResult(selectedYears, monthStart, monthEnd);

  const toggleOpen = () => {
    if (!disabled) {
      setOpen((prev) => !prev);
    }
  };

  return (
    <YearMonthPickerWrapper>
      <LabelContainer>
        {label && (
          <LabelWrapper>
            <PickerLabel>{label}</PickerLabel>
            {required && <RequiredIndicator>*</RequiredIndicator>}
          </LabelWrapper>
        )}
        {tooltip && <SimpleTooltip>{tooltip}</SimpleTooltip>}
      </LabelContainer>

      <DropdownContainer ref={containerRef}>
        <InputWrapper onClick={toggleOpen}>
          <StyledInputField
            value={displayValue}
            readOnly
            disabled={disabled}
            placeholder={placeholderText}
          />
          <IconWrapper disabled={disabled}>
            <IconButton
              data-testid="toggle-year-month-picker"
              onClick={toggleOpen}
              variant="secondary"
              disabled={disabled}
            >
              <Calendar />
            </IconButton>
          </IconWrapper>
        </InputWrapper>

        {open && !disabled && (
          <DropdownPanel>
            <YearSection>
              {yearPickerTitle && <SectionTitle>{yearPickerTitle}</SectionTitle>}

              <YearButtonList
                years={years}
                selectedYears={selectedYears}
                onYearClick={handleYearClick}
              />
            </YearSection>

            {singleYear !== null && (
              <MonthPickerContainer withBorder={!monthPickerTitle}>
                {monthPickerTitle && <SectionTitle>{monthPickerTitle}</SectionTitle>}
                <DatePicker
                  inline
                  showMonthYearPicker
                  selectsRange
                  locale={resolvedLanguage}
                  startDate={monthStart}
                  endDate={monthEnd}
                  minDate={new Date(singleYear, 0, 1)}
                  maxDate={monthPickerMaxDate}
                  onChange={handleMonthRangeChange}
                  renderCustomHeader={() => <span />}
                />
              </MonthPickerContainer>
            )}

            {isMultiYear && (
              <MultiYearMessage>
                {selectedYears[1] - selectedYears[0] + 1} years selected. Month selection is
                disabled.
              </MultiYearMessage>
            )}
          </DropdownPanel>
        )}
      </DropdownContainer>

      {errorMessage && <ErrorMessageBox>{errorMessage}</ErrorMessageBox>}
    </YearMonthPickerWrapper>
  );
};
